/* eslint-disable */
import React, { useState } from 'react';

const USERS_GRID = '1.8fr 2fr 0.8fr 1.6fr 0.8fr 0.4fr';
const ROLES = ['Admin', 'Manager', 'Technician', 'Deployment', 'Read Only'];
const SYS_LOCS = ['In Stock', 'In Testing', 'Quarantine'];

/**
 * DeploymentUserManagement — user role management with Deployment role + location scope.
 * Mirrors the mockup's User Management tab within the Shifts area.
 */
export default function DeploymentUserManagement({ st, setSt, account, audit, isAdmin, SUPER_ADMIN_UPN }) {
  const [editUpn, setEditUpn] = useState(null);
  const [addUpn, setAddUpn] = useState('');
  const [addRole, setAddRole] = useState('Deployment');

  const locs = (st.locations || []).filter(l => !SYS_LOCS.includes(l.name));
  const userEntries = Object.entries(st.userRoles || {});

  const getRoleBadge = (role) => {
    const styles = {
      Admin: { bg: 'var(--brand-tint)', color: 'var(--brand-default)', border: 'var(--brand-border)' },
      Manager: { bg: 'var(--info-fill)', color: 'var(--info-text)', border: 'var(--info-border)' },
      Technician: { bg: 'var(--info-fill)', color: 'var(--info-text)', border: 'var(--info-border)' },
      Deployment: { bg: 'rgba(191,128,255,0.15)', color: '#bf80ff', border: 'rgba(191,128,255,0.4)' },
      'Read Only': { bg: 'var(--surface-inset)', color: 'var(--text-subtle)', border: 'var(--border-default)' },
    };
    const s = styles[role] || styles['Read Only'];
    return (
      <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 9, fontWeight: 700, letterSpacing: 0.3, whiteSpace: 'nowrap', background: s.bg, color: s.color, border: '1px solid ' + s.border }}>
        {role.toUpperCase()}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase();
  };

  const handleAddUser = () => {
    const upn = addUpn.trim();
    if (!upn || upn === SUPER_ADMIN_UPN) return;
    setSt(prev => ({ ...prev, userRoles: { ...prev.userRoles, [upn]: addRole } }));
    audit('user_role_set', { upn, role: addRole });
    setAddUpn('');
  };

  const handleRoleChange = (upn, role) => {
    setSt(prev => ({ ...prev, userRoles: { ...prev.userRoles, [upn]: role } }));
    audit('user_role_changed', { upn, role });
  };

  const handleRemoveUser = (upn) => {
    if (!window.confirm(`Remove ${upn} from user roles?`)) return;
    setSt(prev => {
      const r = { ...prev.userRoles };
      delete r[upn];
      return { ...prev, userRoles: r, userLocationScope: (prev.userLocationScope || []).filter(s => s.userId !== upn) };
    });
    audit('user_removed', { upn });
    if (editUpn === upn) setEditUpn(null);
  };

  const getUserScopes = (upn) => (st.userLocationScope || []).filter(s => s.userId === upn);

  const addScope = (upn, locId) => {
    setSt(prev => ({
      ...prev,
      userLocationScope: [...(prev.userLocationScope || []).filter(s => !(s.userId === upn && s.locationId === locId)), { userId: upn, locationId: locId }],
    }));
  };

  const removeScope = (upn, locId) => {
    setSt(prev => ({
      ...prev,
      userLocationScope: (prev.userLocationScope || []).filter(s => !(s.userId === upn && s.locationId === locId)),
    }));
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-default)', margin: 0 }}>User management</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Roles and location scope. The <strong style={{ color: '#bf80ff' }}>Deployment</strong> role is limited to the on-site scan workflow.
          </div>
        </div>
      </div>

      {/* Users table */}
      <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6, paddingLeft: 2 }}>
        {userEntries.length + 1} user{userEntries.length !== 0 ? 's' : ''}
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: USERS_GRID, gap: 12, padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
          {['User', 'Email', 'Role', 'Location Scope', 'Last Active', ''].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: 0.6, textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>

        {/* Super admin row */}
        <div style={{ display: 'grid', gridTemplateColumns: USERS_GRID, alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface-default)', borderBottom: '1px solid var(--border-default)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-default)', flexShrink: 0 }}>{getInitials(account?.name)}</div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{account?.name || 'Super Admin'}</div>
          </div>
          <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{SUPER_ADMIN_UPN}</div>
          <div>{getRoleBadge('Admin')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>All locations</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Now</div>
          <div />
        </div>

        {/* User rows */}
        {userEntries.map(([upn, role]) => {
          if (upn === SUPER_ADMIN_UPN) return null;
          const scopes = getUserScopes(upn);
          const isEditing = editUpn === upn;
          const nameGuess = upn.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, c => c.toUpperCase());

          return (
            <React.Fragment key={upn}>
              <div
                style={{
                  display: 'grid', gridTemplateColumns: USERS_GRID, alignItems: 'center', gap: 12,
                  padding: '12px 16px', background: isEditing ? 'var(--surface-inset)' : 'var(--surface-default)', borderBottom: '1px solid var(--border-default)',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = 'var(--surface-inset)'; }}
                onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'var(--surface-default)'; }}
              >
                {/* User */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-default)', flexShrink: 0 }}>{getInitials(nameGuess)}</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{nameGuess}</div>
                </div>
                {/* Email */}
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{upn}</div>
                {/* Role */}
                <div>
                  {isEditing ? (
                    <select value={role} onChange={e => handleRoleChange(upn, e.target.value)} className="input" style={{ width: 'auto', padding: '4px 8px', fontSize: 11 }}>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : getRoleBadge(role)}
                </div>
                {/* Location scope */}
                <div style={{ fontSize: 12 }}>
                  {role === 'Deployment' ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      {scopes.map(sc => {
                        const loc = locs.find(l => l.id === sc.locationId);
                        return (
                          <span key={sc.locationId} style={{ padding: '2px 6px 2px 8px', background: 'var(--brand-tint)', border: '1px solid var(--brand-border)', color: 'var(--brand-default)', borderRadius: 999, fontSize: 10, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {loc?.name || sc.locationId}
                            {isEditing && <button onClick={() => removeScope(upn, sc.locationId)} style={{ background: 'transparent', border: 'none', color: 'var(--brand-default)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>&times;</button>}
                          </span>
                        );
                      })}
                      {scopes.length === 0 && <span style={{ color: 'var(--text-ghost)', fontStyle: 'italic', fontSize: 11 }}>No locations</span>}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>All locations</span>
                  )}
                </div>
                {/* Last active */}
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</div>
                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                  <button onClick={() => setEditUpn(isEditing ? null : upn)} className="btn btn-ghost btn-sm">{isEditing ? 'Done' : 'Edit'}</button>
                </div>
              </div>

              {/* Edit panel (expanded below row) */}
              {isEditing && (
                <div style={{ padding: '16px 20px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14, maxWidth: 500 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Role</label>
                      <select value={role} onChange={e => handleRoleChange(upn, e.target.value)} className="input" style={{ width: '100%', fontSize: 13 }}>
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {role === 'Deployment' && (
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>
                          Mobile-only access to the scan workflow. No dashboard, stock, dispatch, test, or settings.
                        </div>
                      )}
                    </div>
                  </div>

                  {role === 'Deployment' && (
                    <div style={{ marginBottom: 14, maxWidth: 500 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                        Location scope <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required for Deployment)</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', minHeight: 36, background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)' }}>
                        {scopes.map(sc => {
                          const loc = locs.find(l => l.id === sc.locationId);
                          return (
                            <span key={sc.locationId} style={{ padding: '3px 8px 3px 10px', background: 'var(--brand-tint)', border: '1px solid var(--brand-border)', color: 'var(--brand-default)', borderRadius: 999, fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {loc?.name || sc.locationId}
                              <button onClick={() => removeScope(upn, sc.locationId)} style={{ background: 'transparent', border: 'none', color: 'var(--brand-default)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>&times;</button>
                            </span>
                          );
                        })}
                        <select
                          onChange={e => { if (e.target.value) { addScope(upn, e.target.value); e.target.value = ''; } }}
                          value=""
                          style={{ background: 'transparent', border: 'none', color: 'var(--brand-default)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '2px 4px' }}
                        >
                          <option value="">+ Add location</option>
                          {locs.filter(l => !scopes.some(sc => sc.locationId === l.id)).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>This user can only see active shifts at these locations.</div>
                    </div>
                  )}

                  <button onClick={() => handleRemoveUser(upn)} className="btn btn-danger btn-sm">Remove user</button>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Add user */}
      <div className="card" style={{ padding: 18, maxWidth: 600 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 12 }}>Add user</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Entra UPN</label>
            <input
              value={addUpn}
              onChange={e => setAddUpn(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddUser(); }}
              placeholder="user@onwave.com"
              className="input"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Role</label>
            <select value={addRole} onChange={e => setAddRole(e.target.value)} className="input" style={{ width: 'auto' }}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button onClick={handleAddUser} disabled={!addUpn.trim()} className="btn btn-primary">Add User</button>
        </div>
      </div>
    </div>
  );
}
